import type { ProductGeometry, Rect } from '../types.ts'

/**
 * Beschnitt-, Endformat- und Sicherheitsbereich als Rechtecke in mm, Ursprung = linke obere
 * Ecke des Endformats (Trim-Box). Der Beschnitt liegt damit bei negativen Koordinaten.
 * `swapped` = Hoch-/Querformat getauscht (Orientierungslogik des Editors).
 */
export function trimSizeMm(g: ProductGeometry, swapped = false): { w: number; h: number } {
  return swapped ? { w: g.trimHeightMm, h: g.trimWidthMm } : { w: g.trimWidthMm, h: g.trimHeightMm }
}

export function fullSizeMm(g: ProductGeometry, swapped = false): { w: number; h: number } {
  const t = trimSizeMm(g, swapped)
  return { w: t.w + 2 * g.bleedMm, h: t.h + 2 * g.bleedMm }
}

export function bleedRectMm(g: ProductGeometry, swapped = false): Rect {
  const f = fullSizeMm(g, swapped)
  return { x: -g.bleedMm, y: -g.bleedMm, w: f.w, h: f.h }
}

export function trimRectMm(g: ProductGeometry, swapped = false): Rect {
  const t = trimSizeMm(g, swapped)
  return { x: 0, y: 0, w: t.w, h: t.h }
}

export function safeRectMm(g: ProductGeometry, swapped = false): Rect {
  const t = trimSizeMm(g, swapped)
  const m = Math.min(g.safeMarginMm, t.w / 2, t.h / 2)
  return { x: m, y: m, w: Math.max(0, t.w - 2 * m), h: Math.max(0, t.h - 2 * m) }
}

export function insetRect(r: Rect, d: number): Rect {
  const dx = Math.min(d, r.w / 2)
  const dy = Math.min(d, r.h / 2)
  return { x: r.x + dx, y: r.y + dy, w: Math.max(0, r.w - 2 * dx), h: Math.max(0, r.h - 2 * dy) }
}

/**
 * Bildet ein mm-Rechteck (relativ zur Trim-Box) auf Pixel ab, wenn die Beschnitt-Box
 * (volles Format) auf `target` (Pixel) liegt. Nützlich für Canvas-Overlays, in denen der
 * Crop-Rahmen das volle Format darstellt.
 */
export function mapRectMmToPx(rectMm: Rect, g: ProductGeometry, target: Rect, swapped = false): Rect {
  const f = fullSizeMm(g, swapped)
  if (!(f.w > 0) || !(f.h > 0)) return { ...target }
  const sx = target.w / f.w
  const sy = target.h / f.h
  return {
    x: target.x + (rectMm.x + g.bleedMm) * sx,
    y: target.y + (rectMm.y + g.bleedMm) * sy,
    w: rectMm.w * sx,
    h: rectMm.h * sy,
  }
}
